import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Heart, 
  Pill,
  AlertTriangle,
  Sparkles,
  Crown,
  Save,
  Lock,
  CheckCircle,
  Shield,
  Info
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const HealthIntakeForm = ({ clientId, clientName, isPremium = false, onUpdate }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    medications: [],
    allergies: [],
    sensitivities: [],
    scalp_conditions: [],
    health_notes: "",
    consent_given: false
  });
  const [inputValues, setInputValues] = useState({
    medications: "",
    allergies: "",
    sensitivities: "",
    scalp_conditions: ""
  });

  const COMMON_ALLERGIES = [
    "PPD (Hair Dye)",
    "Latex",
    "Fragrances",
    "Sulfates",
    "Parabens",
    "Ammonia",
    "Bleach",
    "Nickel"
  ];

  const COMMON_SCALP_CONDITIONS = [
    "Dandruff",
    "Psoriasis",
    "Eczema",
    "Seborrheic Dermatitis",
    "Alopecia",
    "Dry Scalp",
    "Oily Scalp",
    "Sensitive Scalp"
  ];

  useEffect(() => {
    if (clientId && isPremium) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [clientId, isPremium]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/api/health-profile/client/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && Object.keys(response.data).length > 0) {
        setProfile({
          medications: response.data.medications || [],
          allergies: response.data.allergies || [],
          sensitivities: response.data.sensitivities || [],
          scalp_conditions: response.data.scalp_conditions || [],
          health_notes: response.data.health_notes || "",
          consent_given: response.data.consent_given || false
        });
      }
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error("Error fetching health profile:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!profile.consent_given) {
      toast.error("Client consent is required to save health information.");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/api/health-profile/client/${clientId}`, {
        ...profile,
        stylist_id: "" // Will be set by backend
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Health profile saved!");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save health profile");
    } finally {
      setSaving(false);
    }
  };

  const addItem = (field) => {
    const value = inputValues[field].trim();
    if (value && !profile[field].includes(value)) {
      setProfile(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
      setInputValues(prev => ({ ...prev, [field]: "" }));
    }
  };

  const removeItem = (field, item) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].filter(i => i !== item)
    }));
  };

  const toggleQuickAdd = (field, item) => {
    if (profile[field].includes(item)) {
      removeItem(field, item);
    } else {
      setProfile(prev => ({
        ...prev,
        [field]: [...prev[field], item]
      }));
    }
  };

  // Premium Gate
  if (!isPremium) {
    return (
      <Card className="bg-gradient-to-br from-rose-500/10 to-pink-500/10 border-rose-500/30">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-rose-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-rose-400" />
          </div>
          <Badge className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold mb-4">
            <Crown className="w-3 h-3 mr-1" />
            Pro/Elite Feature
          </Badge>
          <h3 className="text-xl font-bold text-white mb-2">Health-Aware Profiles</h3>
          <p className="text-gray-400 mb-4">
            Track client medications, allergies, sensitivities, and scalp conditions for safer services.
          </p>
          <Button className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black">
            Upgrade to Unlock
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  return (
    <Card className="bg-stone-800/50 border-stone-700">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">Health Profile</CardTitle>
              <p className="text-sm text-gray-400">{clientName}</p>
            </div>
          </div>
          <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30">
            <Shield className="w-3 h-3 mr-1" />
            Confidential
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent>
        <Alert className="bg-blue-500/10 border-blue-500/30 mb-6">
          <Info className="w-4 h-4 text-blue-400" />
          <AlertDescription className="text-blue-200 text-sm">
            This information helps provide safer services and better recommendations. All data is kept confidential.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Medications */}
          <div>
            <Label className="text-gray-300 flex items-center gap-2 mb-2">
              <Pill className="w-4 h-4 text-purple-400" />
              Medications
            </Label>
            <div className="flex gap-2">
              <Input
                value={inputValues.medications}
                onChange={(e) => setInputValues(prev => ({ ...prev, medications: e.target.value }))}
                placeholder="Enter medication name..."
                className="bg-stone-800 border-stone-600 text-white"
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addItem("medications"))}
              />
              <Button
                type="button"
                onClick={() => addItem("medications")}
                variant="outline"
                className="border-stone-600 text-gray-300 hover:bg-stone-700"
              >
                Add
              </Button>
            </div>
            {profile.medications.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {profile.medications.map((med, idx) => (
                  <Badge
                    key={idx}
                    variant="secondary"
                    className="bg-purple-500/20 text-purple-300 cursor-pointer hover:bg-purple-500/30"
                    onClick={() => removeItem("medications", med)}
                  >
                    {med} ×
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Allergies */}
          <div>
            <Label className="text-gray-300 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              Allergies
            </Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={inputValues.allergies}
                onChange={(e) => setInputValues(prev => ({ ...prev, allergies: e.target.value }))}
                placeholder="Enter allergy..."
                className="bg-stone-800 border-stone-600 text-white"
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addItem("allergies"))}
              />
              <Button
                type="button"
                onClick={() => addItem("allergies")}
                variant="outline"
                className="border-stone-600 text-gray-300 hover:bg-stone-700"
              >
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {COMMON_ALLERGIES.map(allergy => (
                <Badge
                  key={allergy}
                  variant="outline"
                  className={`cursor-pointer transition-colors ${
                    profile.allergies.includes(allergy)
                      ? "bg-red-500/20 border-red-500 text-red-300"
                      : "border-stone-600 text-gray-400 hover:border-red-500/50"
                  }`}
                  onClick={() => toggleQuickAdd("allergies", allergy)}
                >
                  {profile.allergies.includes(allergy) && <CheckCircle className="w-3 h-3 mr-1" />}
                  {allergy}
                </Badge>
              ))}
            </div>
            {profile.allergies.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {profile.allergies.filter(a => !COMMON_ALLERGIES.includes(a)).map((allergy, idx) => (
                  <Badge
                    key={idx}
                    variant="secondary"
                    className="bg-red-500/20 text-red-300 cursor-pointer hover:bg-red-500/30"
                    onClick={() => removeItem("allergies", allergy)}
                  >
                    {allergy} ×
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Sensitivities */}
          <div>
            <Label className="text-gray-300 flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              Sensitivities
            </Label>
            <div className="flex gap-2">
              <Input
                value={inputValues.sensitivities}
                onChange={(e) => setInputValues(prev => ({ ...prev, sensitivities: e.target.value }))}
                placeholder="Enter sensitivity..."
                className="bg-stone-800 border-stone-600 text-white"
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addItem("sensitivities"))}
              />
              <Button
                type="button"
                onClick={() => addItem("sensitivities")}
                variant="outline"
                className="border-stone-600 text-gray-300 hover:bg-stone-700"
              >
                Add
              </Button>
            </div>
            {profile.sensitivities.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {profile.sensitivities.map((sens, idx) => (
                  <Badge
                    key={idx}
                    variant="secondary"
                    className="bg-amber-500/20 text-amber-300 cursor-pointer hover:bg-amber-500/30"
                    onClick={() => removeItem("sensitivities", sens)}
                  >
                    {sens} ×
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Scalp Conditions */}
          <div>
            <Label className="text-gray-300 flex items-center gap-2 mb-2">
              🔬 Scalp Conditions
            </Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {COMMON_SCALP_CONDITIONS.map(condition => (
                <Badge
                  key={condition}
                  variant="outline"
                  className={`cursor-pointer transition-colors ${
                    profile.scalp_conditions.includes(condition)
                      ? "bg-teal-500/20 border-teal-500 text-teal-300"
                      : "border-stone-600 text-gray-400 hover:border-teal-500/50"
                  }`}
                  onClick={() => toggleQuickAdd("scalp_conditions", condition)}
                >
                  {profile.scalp_conditions.includes(condition) && <CheckCircle className="w-3 h-3 mr-1" />}
                  {condition}
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={inputValues.scalp_conditions}
                onChange={(e) => setInputValues(prev => ({ ...prev, scalp_conditions: e.target.value }))}
                placeholder="Add other condition..."
                className="bg-stone-800 border-stone-600 text-white"
                onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addItem("scalp_conditions"))}
              />
              <Button
                type="button"
                onClick={() => addItem("scalp_conditions")}
                variant="outline"
                className="border-stone-600 text-gray-300 hover:bg-stone-700"
              >
                Add
              </Button>
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <Label className="text-gray-300 mb-2">Additional Health Notes</Label>
            <Textarea
              value={profile.health_notes}
              onChange={(e) => setProfile(prev => ({ ...prev, health_notes: e.target.value }))}
              placeholder="Any other health information relevant to hair services..."
              className="bg-stone-800 border-stone-600 text-white"
              rows={3}
            />
          </div>

          {/* Consent */}
          <div className="bg-stone-900/50 rounded-xl p-4 border border-stone-700">
            <div className="flex items-start gap-3">
              <Checkbox
                id="consent"
                checked={profile.consent_given}
                onCheckedChange={(checked) => setProfile(prev => ({ ...prev, consent_given: checked }))}
                className="mt-1 border-amber-500 data-[state=checked]:bg-amber-500 data-[state=checked]:text-black"
              />
              <div>
                <Label htmlFor="consent" className="text-white cursor-pointer">
                  Client Consent *
                </Label>
                <p className="text-xs text-gray-400 mt-1">
                  Client has provided consent to store health information for the purpose of providing safer and better hair services. 
                  This information will be kept confidential and only shared with service providers.
                </p>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={saving || !profile.consent_given}
            className="w-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                Save Health Profile
              </span>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default HealthIntakeForm;
